import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { pricingPackages } from '../../../mocks/servicesData';

export default function PricingPackages() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });

  return (
    <section id="pricing" className="py-28 bg-stone-50 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-300/30 to-transparent"></div>
      <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl"></div>

      <div className="max-w-6xl mx-auto px-6 relative">
        <div
          ref={headerRef}
          className={`text-center mb-16 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-400"></div>
            <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">
              Transparent Pricing
            </span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-400"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-normal text-stone-900 mb-5">
            Choose Your Package
          </h2>
          <p className="text-stone-500 max-w-2xl mx-auto leading-relaxed">
            Three flexible tiers designed to match every event size and budget. All packages are fully customisable — just ask.
          </p>
        </div>

        <div
          ref={gridRef}
          className={`grid md:grid-cols-3 gap-6 stagger-children ${gridVisible ? 'visible' : ''}`}
        >
          {pricingPackages.map((pkg) => (
            <div
              key={pkg.id}
              onMouseEnter={() => setHoveredId(pkg.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={`relative bg-white rounded-2xl overflow-hidden border transition-all duration-500 cursor-pointer ${
                pkg.popular
                  ? 'border-amber-300 shadow-xl shadow-amber-100/50 scale-[1.02]'
                  : 'border-stone-200 shadow-sm hover:shadow-xl hover:border-amber-200/50'
              }`}
            >
              {pkg.popular && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-yellow-400"></div>
              )}

              <div className="p-8">
                {pkg.popular && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 text-xs font-semibold rounded-full mb-4">
                    <i className="ri-star-fill text-amber-500"></i>
                    Most Popular
                  </div>
                )}

                <h3 className="text-2xl font-display text-stone-900">{pkg.name}</h3>
                <p className="text-stone-400 text-sm mt-1 mb-6">{pkg.tagline}</p>

                <div className="mb-6">
                  <span className="text-stone-400 text-xs uppercase tracking-wider">{pkg.priceLabel}</span>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-4xl font-display text-stone-900">${pkg.price.toLocaleString()}</span>
                    <span className="text-stone-400 text-sm">{pkg.priceSuffix}</span>
                  </div>
                </div>

                <div className="w-full h-px bg-stone-100 mb-6"></div>

                <div className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        feature.included ? 'bg-amber-50' : 'bg-stone-50'
                      }`}>
                        <i className={`text-xs ${
                          feature.included ? 'ri-check-line text-amber-600' : 'ri-close-line text-stone-300'
                        }`}></i>
                      </div>
                      <span className={`text-sm ${feature.included ? 'text-stone-700' : 'text-stone-300'}`}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <a
                  href="#consultation"
                  className={`block w-full text-center py-3.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                    pkg.popular
                      ? 'bg-gradient-to-r from-amber-500 to-yellow-400 text-stone-900 hover:from-amber-400 hover:to-yellow-300 shadow-lg shadow-amber-500/20'
                      : hoveredId === pkg.id
                        ? 'bg-stone-900 text-white'
                        : 'bg-stone-100 text-stone-700 hover:bg-stone-900 hover:text-white'
                  }`}
                >
                  {pkg.cta}
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-stone-400 text-sm">
            All prices in AUD. GST may apply. Custom quotes available for multi-day events.
          </p>
        </div>
      </div>
    </section>
  );
}
